/**
 * UnifiedGatesPanel — os 5 Quality Gates unificados no Live Board, aprováveis
 * ao vivo num só lugar (#3/#4), + strip de ciclos.
 *
 * Modelo unificado (GET /projects/{id}/journey → gates[]): cada gate traz
 * `kind` ('execution' = gate_a/b via signal | 'phase' = journey_gates) e
 * `status`. Aprovação:
 *   - kind 'phase' (tecnologia/escopo): PATCH inline /journey-gates/{key}.
 *   - kind 'phase' 'final' (aceite duplo): botões Negócio/Técnico (role).
 *   - kind 'execution' (negocio/squads): abre a tela de revisão do gate (diffs).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { C } from '@/theme/palette';

const STATUS_STYLE = {
  approved:         { label: 'Aprovado',  color: C.cyber },
  rejected:         { label: 'Rejeitado', color: C.hot },
  pending:          { label: 'Pendente',  color: C.warn },
  nao_implementado: { label: '—',         color: C.neutralMuted },
};

const CYCLE_COLOR = { done: C.cyber, running: C.warn, planned: C.neutralMuted };

function btnStyle(color, disabled) {
  return {
    fontSize: 11,
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: C.black,
    backgroundColor: color,
    border: `1px solid ${color}`,
    opacity: disabled ? 0.5 : 1,
  };
}

/**
 * CycleStrip — stepper PROMINENTE dos ciclos de desenvolvimento.
 * `current` (1-based) vem do log "🔄 Ciclo N/M"; senão deriva do status dos ciclos.
 *
 * `consolidated` = a entrega foi gerada numa passada ÚNICA cobrindo TODOS os ciclos
 * (o codegen consolida o escopo — não roda 1 passe por ciclo). Nesse modo não há
 * progressão 1→2→3: quando `allDone`, marcamos todos os ciclos como ✓ concluídos
 * de uma vez; enquanto roda, todos ficam "em andamento". Sem isto a UI mostrava
 * "Ciclo 1/N" com o resto pendente e parecia que ciclos foram pulados.
 */
export function CycleStrip({ cycles, current, consolidated = false, allDone = false }) {
  if (!cycles?.length) return null;
  const total = cycles.length;

  let cur = Number(current) || 0;
  if (!cur) {
    const ip = cycles.findIndex((c) => ['in_progress', 'running'].includes(c.status));
    if (ip >= 0) cur = ip + 1;
    else {
      const done = cycles.filter((c) => ['done', 'completed'].includes(c.status)).length;
      cur = Math.min(total, done + 1);
    }
  }
  // Consolidado + concluído: tudo ✓ (cur além do fim → todo pos < cur = done).
  if (consolidated && allDone) cur = total + 1;
  const curTheme = cycles[cur - 1]?.theme;

  return (
    <div style={{ marginTop: 10 }} aria-label={consolidated
      ? `Entrega consolidada · ${total} ciclos${allDone ? ' concluídos' : ''}`
      : `Ciclo ${cur} de ${total}`}>
      {/* Cabeçalho grande */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {consolidated ? (
          <span style={{ fontSize: 15, fontWeight: 800, color: C.cyber }}>
            {allDone ? '✅' : '🎯'} Entrega consolidada{' '}
            <span style={{ color: C.neutralMuted, fontWeight: 700 }}>
              · {total} ciclos {allDone ? 'concluídos' : 'em uma passada'}
            </span>
          </span>
        ) : (
          <span style={{ fontSize: 15, fontWeight: 800, color: C.cyber }}>
            🔄 Ciclo {cur} <span style={{ color: C.neutralMuted, fontWeight: 700 }}>de {total}</span>
          </span>
        )}
        {!consolidated && curTheme && (
          <span style={{ fontSize: 12.5, color: C.neutral, fontWeight: 600 }}>· {curTheme}</span>
        )}
      </div>

      {/* Stepper: cada ciclo numerado, o atual em destaque */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        {cycles.map((c, i) => {
          const pos = i + 1;
          // Consolidado em andamento: nenhum "atual" isolado — todos ativos juntos.
          const state = consolidated
            ? (allDone ? 'done' : 'current')
            : (pos < cur ? 'done' : pos === cur ? 'current' : 'pending');
          const col = state === 'done' ? C.cyber : state === 'current' ? C.cyber : C.neutralMuted;
          return (
            <div key={c.number ?? i} style={{ flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <span style={{ flex: 1, height: 2, background: i === 0 ? 'transparent' : ((consolidated || pos <= cur) ? C.cyber : C.blackBorder) }} />
                <span style={{
                  width: state === 'current' ? 30 : 26, height: state === 'current' ? 30 : 26,
                  borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                  fontSize: 12, fontWeight: 800,
                  color: state === 'pending' ? C.neutralMuted : '#0b0b0b',
                  background: state === 'pending' ? 'transparent' : col,
                  border: `2px solid ${col}`,
                  boxShadow: state === 'current' ? `0 0 10px ${C.cyber}` : 'none',
                }}>
                  {state === 'done' ? '✓' : pos}
                </span>
                <span style={{ flex: 1, height: 2, background: i === cycles.length - 1 ? 'transparent' : ((consolidated || pos < cur) ? C.cyber : C.blackBorder) }} />
              </div>
              <span style={{ marginTop: 5, fontSize: 10, textAlign: 'center', lineHeight: 1.25,
                color: state === 'current' ? C.neutral : C.neutralMuted,
                fontWeight: state === 'current' ? 700 : 500,
                whiteSpace: 'normal', overflow: 'hidden', maxWidth: 120,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {c.theme || `Ciclo ${c.number ?? pos}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function GatesPanel({ projectId, gates, onReviewExecutionGate }) {
  const api = useApi();
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: ({ gateKey, body }) =>
      api.patch(`/projects/${projectId}/journey-gates/${gateKey}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectId, 'journey'] }),
  });

  if (!gates?.length) return null;

  return (
    <div
      style={{
        padding: '10px 20px',
        backgroundColor: C.blackElev,
        borderBottom: `1px solid ${C.blackBorder}`,
        flexShrink: 0,
      }}
      aria-label="Quality Gates"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: C.neutralDim }}>
          QUALITY GATES (5) · aprove aqui mesmo
        </span>
        {decide.isError && <span style={{ fontSize: 11, color: C.hot }}>Falha ao aprovar (sem permissão?)</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {gates.map((g) => {
          const s = STATUS_STYLE[g.status] ?? STATUS_STYLE.pending;
          const isPending = g.status === 'pending';
          return (
            <div
              key={g.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: '8px 10px',
                minWidth: 152,
                borderRadius: 8,
                backgroundColor: C.black,
                border: `1px solid ${isPending ? C.warn : C.blackBorder}`,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: C.neutral }}>{g.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: 99, backgroundColor: s.color }} aria-hidden="true" />
                <span style={{ fontSize: 11, color: s.color }}>
                  {s.label}{g.waiver ? ' (waiver)' : ''}
                </span>
              </div>

              {isPending && g.kind === 'execution' && (
                <button type="button" onClick={() => onReviewExecutionGate?.(g)} style={btnStyle(C.cyber, false)}>
                  Revisar →
                </button>
              )}
              {isPending && g.kind === 'phase' && g.key !== 'final' && (
                <button
                  type="button"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ gateKey: g.key, body: { decision: 'approve' } })}
                  style={btnStyle(C.cyber, decide.isPending)}
                >
                  {decide.isPending ? 'Aprovando…' : 'Aprovar'}
                </button>
              )}
              {isPending && g.kind === 'phase' && g.key === 'final' && (
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ gateKey: 'final', body: { decision: 'approve', role: 'business' } })}
                    style={btnStyle(C.cyber, decide.isPending)}
                  >
                    Negócio
                  </button>
                  <button
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ gateKey: 'final', body: { decision: 'approve', role: 'technical' } })}
                    style={btnStyle(C.quantum, decide.isPending)}
                  >
                    Técnico
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
